/**
 * The `<main data-navigation-root="main-content">` class each page ships with — transcribed from the
 * JTE templates that render it, for the one navigation path that has no document to read it from.
 *
 * <p><b>Why this file has to exist.</b> A **client route** (thin-shell route) mounts locally with no
 * HTML fetch: `EnhancedNavigation.mountClientOwnedRoute` keeps the `<main>` element the previous page
 * left behind and only replaces its children. Ordinary document-swap soft navigation gets the
 * destination's shell for free — `DocumentSwap.syncElementAttributes` copies the incoming element's
 * attributes — but a client route has no incoming element, so whatever `<main>` was wearing stays on.
 *
 * <p><b>The defect that produced it (2026-08-15).</b> `messages.jte` gives `/messages` a shell that
 * makes the page own exactly one viewport: `fixed`, sized to the measured visual viewport,
 * `overflow-hidden`, a flex column. `ChatExperience`'s page shell deliberately declares no height of
 * its own and inherits that column. `/messages` is also a client route — so reaching it from inside
 * the app left `<main>` on `basic_layout`'s default (in flow, `min-h-full`, `pb-24`), the island's
 * `flex-1` resolved against nothing, the shell took its content height, and the page scrolled as a
 * document with the composer below the fold. A hard refresh of the same URL was correct, which is the
 * signature of a server-render-only mechanism. Diagnosis:
 * `designs/01-now/2026-08-11-mobile-chat-viewport-ownership-and-composer-design.md` §3d, Stage 8.
 *
 * <p><b>These strings are pinned to the templates by `navigationRootShell.contract.test.ts`</b>, which
 * reads the `.jte` files off disk. That guard is not optional bookkeeping: without it this trades a
 * missing shell for a drifting one, which is strictly worse — the wrong shell renders confidently, and
 * only on the soft path, so nobody sees it. Whitespace is normalized on both sides; nothing else is.
 */

/**
 * `layouts/basic_layout.jte` — the `mainClass` default, worn by every page that does not override it.
 *
 * SV-157 A3 (2026-10-05): the top reserve is BREAKPOINTED at `md`. Below 768 px there is no site header, so the
 * reserve is the safe area plus the banner stack only; from `md` up it is the header's `4.5rem` as before. The
 * three shells below follow the same split, each mirroring its template (`navigationRootShell.contract.test.ts`).
 *
 * `DISCOVERY-165` (r125 A11, built r179): the default shell adds ONE page-heading top spacing, `1rem`. Below `md` the
 * reserve is `max(safe-area inset, 1rem)` plus the banner (a mobile browser's inset is 0, so the first heading no longer
 * sits ~6 px under the address bar; the app's larger inset is unchanged), and from `md` it is `5.5rem` (the header's
 * `4.5rem` plus the same `1rem`). The messages and editor shells keep their own reserves.
 *
 * The sub-`sm` gutter is `px-4` and not `px-2` since 2026-08-19 (`LANDING-13`): every uncarded line on
 * every page sat 8px from the screen edge while the cards beside it sat at 32px, and both platform
 * guidelines put the floor at 16px. `px-4 sm:px-4` reads redundant on purpose — it says the two steps
 * are now deliberately equal rather than that one was deleted.
 */
export const DEFAULT_NAVIGATION_ROOT_CLASS_NAME
    = "relative mx-auto w-full max-w-screen-xl px-4 sm:px-4 lg:px-6"
    + " pt-[calc(max(env(safe-area-inset-top),1rem)+var(--seavien-connection-banner-height,0px))]"
    + " md:pt-[calc(5.5rem+env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))]"
    + " pb-24 lg:pb-6 min-h-full font-[var(--standard-font)]";

/**
 * `layouts/basic_layout.jte` with `phoneEdgeToEdge = true` — the default shell plus `max-sm:px-0`.
 *
 * SV-138 (FRR `3a`, 2026-09-26): below `sm` the page pays no gutter so content rows run to the screen
 * edge and carry their own 16px inset; from `sm` up nothing changes. Worn by exactly five pages — home,
 * discussion, group, profile and search (decision A29, opt-in, never global). Three of them are client
 * routes (home, search, profile), which keep the previous page's `<main>`, so without this constant an
 * in-app arrival at home from settings would keep settings' gutter, and the reverse would lose it.
 */
export const EDGE_NAVIGATION_ROOT_CLASS_NAME = `${DEFAULT_NAVIGATION_ROOT_CLASS_NAME} max-sm:px-0`;

/**
 * `messages/messages.jte` — the one-viewport chat shell.
 *
 * Every token here is load-bearing and explained in that template's own comment: `fixed` +
 * `top-[var(--seavien-app-viewport-offset-top)]` keeps the shell on the visible rectangle when iOS
 * scrolls the visual viewport for the keyboard, `h-[var(--seavien-app-viewport-height)]` takes the
 * measured height rather than a declared length, and `overflow-hidden` + `pb-0` are what stop the
 * document being taller than one viewport.
 *
 * SV-157 (2026-10-05): the pair is now phone | `md`+, not `sm`. Below 768 px there is no site header, so the
 * reserve is the safe area plus the banner; from `md` up it is the `4.5rem` the `sm` step carried. The history:
 *
 * The top reserve is a RESPONSIVE PAIR since 2026-08-22 (wave 8, chat run-2
 * `chat-phone-shell-draws-a-hairline-against-the-bezel`): the site header is `4rem` below `sm` and
 * `4.5rem` from `sm` up, so a single `4.5rem` reserve over-padded the phone by 8px and produced a dead
 * strip between the header and the panel. Both halves must keep mirroring `messages/messages.jte` —
 * `navigationRootShell.contract.test.ts` reads that template and compares, and it is the only thing
 * that notices when one side moves alone.
 */
export const MESSAGES_NAVIGATION_ROOT_CLASS_NAME
    = "fixed inset-x-0 top-[var(--seavien-app-viewport-offset-top,0px)] mx-auto flex"
    + " h-[var(--seavien-app-viewport-height)] w-full max-w-screen-xl flex-col overflow-hidden"
    + " px-0 sm:px-4 lg:px-6"
    + " pt-[calc(env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))]"
    + " md:pt-[calc(4.5rem+env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))]"
    + " pb-0 md:pb-2 font-[var(--standard-font)]";

/**
 * `layouts/editor_layout.jte` — the discussion editor's shell.
 *
 * Full-bleed (`px-0`, no `max-w-*`) with a shorter header reserve than the default, because the editor
 * contributes its own bottom action bar and its own gutters. `/create/discussion**` is a client route,
 * so until 2026-08-15 a soft-navigated editor silently wore the default layout's gutters,
 * `max-w-screen-xl` and `pb-24` while a full load of the same URL did not.
 */
export const EDITOR_NAVIGATION_ROOT_CLASS_NAME
    = "relative w-full px-0"
    + " pt-[calc(env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))]"
    + " md:pt-[calc(4rem+env(safe-area-inset-top)+var(--seavien-connection-banner-height,0px))]"
    + " font-[var(--standard-font)] flex flex-col items-stretch";

/**
 * The mount container `messages.jte` renders inside its shell.
 *
 * The second half of the same gap: a correct `<main>` is not enough if the container between it and
 * the island is not a flex item. This route's `mountClassName` was `react-component w-full`.
 */
export const MESSAGES_MOUNT_CLASS_NAME = "react-component flex min-h-0 w-full flex-1 flex-col";

/**
 * The attribute `basic_layout.jte` renders from its `viewportContract` parameter, and the selector the
 * I2 probe (`lib/unusablePageSignals.ts`) scopes itself with.
 *
 * <p><b>It belongs in this file for exactly the reason the class names do, and it is the same defect
 * one field over.</b> A client route keeps the `<main>` the previous page left behind, so a soft
 * navigation into `/messages` would arrive wearing whichever contract the PREVIOUS page declared —
 * usually none. The probe would then find no contract on the one surface it was built for, and only on
 * the in-app path, which is the majority path. That is a detector going dark with nothing to show for
 * it, which is worse than the class-drift case: a wrong shell renders a visibly wrong page, while a
 * missing contract renders a perfectly correct one and simply stops watching.
 *
 * <p>The values below are pinned to the templates by `navigationRootShell.contract.test.ts`, the same
 * guard that pins the class lists.
 */
export const VIEWPORT_CONTRACT_ATTRIBUTE = "data-viewport-contract";

/** `messages/messages.jte` — the surface the whole signal was built for. */
export const MESSAGES_VIEWPORT_CONTRACT = "chat";

/** `layouts/editor_layout.jte` — the same keyboard-inset machinery, one surface over. */
export const EDITOR_VIEWPORT_CONTRACT = "editor";
